/**
 * Brand Color Management Utilities
 * Handles consistent brand colors across all charts
 */

// Default fallback palette (matches backend)
export const DEFAULT_PALETTE = [
  "#FF6B35", "#2E86AB", "#A23B72", "#F18F01", "#C73E1D", 
  "#6A994E", "#7209B7", "#F72585", "#264653", "#2A9D8F",
  "#E9C46A", "#F4A261", "#E76F51", "#8338EC", "#3A86FF",
  "#06FFA5", "#FFBE0B", "#FB5607", "#FF006E", "#D90368"
];

// Cache for brand colors to prevent redundant API calls
let brandColorsCache = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get brand color configuration from API
 */
export const fetchBrandColors = async (axiosClient) => {
  // Check if we have recent cached data
  const now = Date.now();
  if (brandColorsCache && (now - cacheTimestamp) < CACHE_DURATION) {
    return brandColorsCache;
  }

  try {
    const response = await axiosClient.get('/api/brand-colors');
    // Update cache
    brandColorsCache = response.data;
    cacheTimestamp = now;
    return response.data;
  } catch (error) {
    console.error('Error fetching brand colors:', error);
    // Return default structure if API fails
    const defaultData = {
      brand_color_mappings: {},
      fallback_palette: DEFAULT_PALETTE,
      next_color_index: 0
    };
    // Cache the default data too
    brandColorsCache = defaultData;
    cacheTimestamp = now;
    return defaultData;
  }
};

/**
 * Clear the brand colors cache (call this when colors are updated)
 */
export const clearBrandColorsCache = () => {
  brandColorsCache = null;
  cacheTimestamp = 0;
};

/**
 * Update brand color configuration via API
 */
export const updateBrandColors = async (axiosClient, brandColors) => {
  try {
    const response = await axiosClient.put('/api/brand-colors', brandColors);
    return response.data;
  } catch (error) {
    console.error('Error updating brand colors:', error);
    throw error;
  }
};

/**
 * Assign a color to a brand (auto or manual)
 */
export const assignBrandColor = async (axiosClient, brandName, color = null) => {
  try {
    const response = await axiosClient.post('/api/brand-colors/assign', {
      brand_name: brandName,
      color: color
    });
    return response.data;
  } catch (error) {
    console.error('Error assigning brand color:', error);
    throw error;
  }
};

/**
 * Get color for a specific brand
 * Auto-assigns if not already assigned
 */
export const getBrandColor = async (axiosClient, brandName, brandColors = null) => {
  // If brandColors provided, use it directly
  if (brandColors && brandColors.brand_color_mappings[brandName]) {
    return brandColors.brand_color_mappings[brandName];
  }
  
  // Otherwise, fetch from API and auto-assign if needed
  try {
    const result = await assignBrandColor(axiosClient, brandName);
    return result.color;
  } catch (error) {
    console.error(`Error getting color for brand ${brandName}:`, error);
    // Fallback to a default color
    const index = brandName.length % DEFAULT_PALETTE.length;
    return DEFAULT_PALETTE[index];
  }
};

/**
 * Get colors for multiple brands
 * Returns an object mapping brand names to colors
 * Ensures consistent color assignment across the application.
 */
export const getBrandColors = async (axiosClient, requestedBrandNames, allOrgBrands = [], brandColorConfig = null) => {
  const colors = {};
  let config = brandColorConfig;

  if (!config) {
    config = await fetchBrandColors(axiosClient);
  }

  const { brand_color_mappings, fallback_palette } = config;

  // Ensure allOrgBrands is populated if not provided (e.g., from a context)
  // For now, we assume it's passed or we work only with requestedBrandNames if it's empty.
  // A more robust solution would fetch all brands for the org if allOrgBrands is empty.
  const uniqueSortedBrands = [...new Set(allOrgBrands.length > 0 ? allOrgBrands : requestedBrandNames)].sort();

  const finalColorMappings = { ...brand_color_mappings };

  // Assign stable default colors based on alphabetical position
  // Each brand gets the same fallback color index regardless of custom assignments
  uniqueSortedBrands.forEach((brandName, index) => {
    if (!finalColorMappings[brandName]) {
      // Brand not in existing mappings, assign from fallback palette using stable index
      finalColorMappings[brandName] = fallback_palette[index % fallback_palette.length];
    }
  });

  // Extract colors for the specifically requested brand names
  for (const brandName of requestedBrandNames) {
    if (finalColorMappings[brandName]) {
      colors[brandName] = finalColorMappings[brandName];
    } else {
      // If a requested brand was somehow not in uniqueSortedBrands (should not happen if allOrgBrands is comprehensive)
      // assign a fallback color to prevent errors using the brand name hash as fallback
      console.warn(`Brand ${brandName} was not found in the initial sorted list for color assignment. Assigning a hash-based fallback.`);
      const hashIndex = brandName.length % fallback_palette.length;
      colors[brandName] = fallback_palette[hashIndex];
    }
  }
  
  // Ensure we always return a proper object with string values
  return colors;
};

/**
 * Transform colors for dark mode
 * Adjusts brightness and saturation for better dark mode visibility
 */
export const transformColorForDarkMode = (color) => {
  // Convert hex to HSL
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16) / 255;
  const g = parseInt(hex.substr(2, 2), 16) / 255;
  const b = parseInt(hex.substr(4, 2), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  
  // Adjust for dark mode: increase lightness and slightly reduce saturation
  l = Math.min(0.8, l + 0.2); // Increase lightness
  s = Math.max(0.3, s * 0.8); // Slightly reduce saturation
  
  // Convert back to hex
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  
  let r2, g2, b2;
  if (s === 0) {
    r2 = g2 = b2 = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r2 = hue2rgb(p, q, h + 1/3);
    g2 = hue2rgb(p, q, h);
    b2 = hue2rgb(p, q, h - 1/3);
  }
  
  const toHex = (c) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(r2)}${toHex(g2)}${toHex(b2)}`;
};

/**
 * Get appropriate color based on theme
 */
export const getThemeAwareColor = (color, isDarkMode = false) => {
  return isDarkMode ? transformColorForDarkMode(color) : color;
}; 